import { apiClient } from './client';
import type { AiConversation, AiProviderConfig } from '@comp-sys/shared';

export interface SSEChatEvent {
  type: string;
  [key: string]: unknown;
}

export const aiApi = {
  listConversations: () =>
    apiClient.get<AiConversation[]>('ai/conversations'),

  createConversation: (title?: string) =>
    apiClient.post<AiConversation>('ai/conversations', { title }),

  getConversation: (id: string) =>
    apiClient.get<AiConversation>(`ai/conversations/${id}`),

  deleteConversation: (id: string) =>
    apiClient.delete<void>(`ai/conversations/${id}`),

  chat: async (content: string, conversationId?: string, autoApprove?: boolean): Promise<ReadableStream<Uint8Array>> => {
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const token = document.cookie
      .split('; ')
      .find((row) => row.startsWith('accessToken='))
      ?.split('=')[1];

    const response = await fetch(`${baseUrl}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content, conversationId, autoApprove }),
    });
    if (!response.ok) throw new Error(`Chat request failed: ${response.status}`);
    return response.body as ReadableStream<Uint8Array>;
  },

  approve: async (requestId: string, autoApprove?: boolean): Promise<ReadableStream<Uint8Array>> => {
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const token = document.cookie
      .split('; ')
      .find((row) => row.startsWith('accessToken='))
      ?.split('=')[1];

    const response = await fetch(`${baseUrl}/api/ai/approve/${requestId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ autoApprove }),
    });
    if (!response.ok) throw new Error(`Approve request failed: ${response.status}`);
    // SSE response — return stream for caller to process
    return response.body as ReadableStream<Uint8Array>;
  },

  reject: (requestId: string) =>
    apiClient.post<{ success: boolean }>(`ai/reject/${requestId}`, {}),

  continueChat: async (conversationId: string, autoApprove?: boolean): Promise<ReadableStream<Uint8Array>> => {
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const token = document.cookie
      .split('; ')
      .find((row) => row.startsWith('accessToken='))
      ?.split('=')[1];

    const response = await fetch(`${baseUrl}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content: '', conversationId, autoApprove }),
    });
    if (!response.ok) throw new Error(`Chat request failed: ${response.status}`);
    return response.body as ReadableStream<Uint8Array>;
  },

  getProviderConfig: () =>
    apiClient.get<Partial<AiProviderConfig>>('ai/provider-config'),

  updateProviderConfig: (config: Partial<AiProviderConfig>) =>
    apiClient.put<unknown>('ai/provider-config', config),

  testConnection: () =>
    apiClient.post<{ success: boolean; error?: string }>('ai/test-connection', {}),
};
